import { useEffect, useState, useRef } from 'react';
import { IControl, useMap } from 'react-map-gl/maplibre';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { Button } from '@/components/ui/button';
import { 
  GeofenceCircleGeometry, 
  GeofenceGeometry, 
  GeofenceType,
  GeofencePolygonGeometry
} from '@/api/v1/geofence-api';
import { createCirclePoints } from '@/lib/geo-utils';

interface GeofenceDrawProps {
  onGeofenceDrawn: (geometry: GeofenceGeometry, type: GeofenceType) => void;
  initialGeometry?: GeofenceGeometry;
  readOnly?: boolean;
}

export function GeofenceDraw({ onGeofenceDrawn, initialGeometry, readOnly = false }: GeofenceDrawProps) {
  const { current: map } = useMap();
  const drawRef = useRef<MapboxDraw | null>(null);
  const [drawMode, setDrawMode] = useState<'polygon' | 'circle'>('polygon');

  useEffect(() => {
    if (!map) return;

    // Initialize MapboxDraw with custom blue styling
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true
      },
      defaultMode: 'draw_polygon',
      styles: [
        // Polygon fill
        {
          id: 'gl-draw-polygon-fill-inactive',
          type: 'fill',
          filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: {
            'fill-color': '#0080FF',
            'fill-opacity': 0.15
          }
        },
        {
          id: 'gl-draw-polygon-fill-active',
          type: 'fill',
          filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
          paint: {
            'fill-color': '#0080FF',
            'fill-opacity': 0.15
          }
        },
        // Polygon outline
        {
          id: 'gl-draw-polygon-stroke-inactive',
          type: 'line',
          filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#0080FF',
            'line-width': 2
          }
        },
        {
          id: 'gl-draw-polygon-stroke-active',
          type: 'line',
          filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#0080FF',
            'line-width': 2
          }
        },
        // Vertex points
        {
          id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          paint: {
            'circle-radius': 5,
            'circle-color': '#fff'
          }
        },
        {
          id: 'gl-draw-polygon-and-line-vertex-inactive',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          paint: {
            'circle-radius': 3,
            'circle-color': '#0080FF'
          }
        },
        // Midpoints
        {
          id: 'gl-draw-polygon-midpoint',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
          paint: {
            'circle-radius': 3,
            'circle-color': '#0080FF'
          }
        },
        // Line
        {
          id: 'gl-draw-line-inactive',
          type: 'line',
          filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#0080FF',
            'line-width': 2
          }
        },
        {
          id: 'gl-draw-line-active',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#0080FF',
            'line-width': 2
          }
        }
      ]
    });

    map.addControl(draw as unknown as IControl);
    drawRef.current = draw;

    // Handle drawing events
    const handleDrawCreate = (e: { features: Array<{ geometry: { coordinates: number[][][] } }> }) => {
      const features = e.features;
      if (features && features.length > 0) {
        const feature = features[0];
        
        if (drawMode === 'polygon') {
          const coordinates = feature.geometry.coordinates;
          const geometry: GeofencePolygonGeometry = {
            type: 'polygon',
            coordinates
          };
          onGeofenceDrawn(geometry, 'polygon');
        } else {
          // For circle mode, we'll need to calculate center and radius
          const coordinates = feature.geometry.coordinates[0];
          let sumLng = 0;
          let sumLat = 0;
          
          // Skip the last point as it's the same as the first in a closed polygon
          const points = coordinates.length > 0 ? coordinates.slice(0, -1) : coordinates;
          
          for (const point of points) {
            sumLng += point[0];
            sumLat += point[1];
          }
          
          const center: [number, number] = [
            sumLng / points.length,
            sumLat / points.length
          ];
          
          // Calculate the radius (distance from center to farthest point)
          let maxDistance = 0;
          for (const point of points) {
            // Simple Euclidean distance for now
            const dx = center[0] - point[0];
            const dy = center[1] - point[1];
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > maxDistance) {
              maxDistance = distance;
            }
          }
          
          // Convert to meters (approximate)
          const radiusInMeters = Math.round(maxDistance * 111319.9);
          
          const geometry: GeofenceCircleGeometry = {
            type: 'circle',
            center,
            radius: radiusInMeters
          };
          onGeofenceDrawn(geometry, 'circle');
        }
      }
    };

    const handleDrawUpdate = (e: { features: Array<{ geometry: { coordinates: number[][][] } }> }) => {
      const features = e.features;
      if (features && features.length > 0) {
        const feature = features[0];
        
        if (drawMode === 'polygon') {
          const coordinates = feature.geometry.coordinates;
          const geometry: GeofencePolygonGeometry = {
            type: 'polygon',
            coordinates
          };
          onGeofenceDrawn(geometry, 'polygon');
        } else {
          // Handle circle update similar to create
          const coordinates = feature.geometry.coordinates[0];
          let sumLng = 0;
          let sumLat = 0;
          
          const points = coordinates.length > 0 ? coordinates.slice(0, -1) : coordinates;
          
          for (const point of points) {
            sumLng += point[0];
            sumLat += point[1];
          }
          
          const center: [number, number] = [
            sumLng / points.length,
            sumLat / points.length
          ];
          
          let maxDistance = 0;
          for (const point of points) {
            const dx = center[0] - point[0];
            const dy = center[1] - point[1];
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > maxDistance) {
              maxDistance = distance;
            }
          }
          
          const radiusInMeters = Math.round(maxDistance * 111319.9);
          
          const geometry: GeofenceCircleGeometry = {
            type: 'circle',
            center,
            radius: radiusInMeters
          };
          onGeofenceDrawn(geometry, 'circle');
        }
      }
    };

    const handleDrawDelete = () => {
      onGeofenceDrawn({ type: 'polygon', coordinates: [] } as GeofencePolygonGeometry, 'polygon');
    };

    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    map.on('draw.delete', handleDrawDelete);

    // Cleanup
    return () => {
      if (!map || !drawRef.current) return;
      try {
        map.off('draw.create', handleDrawCreate);
        map.off('draw.update', handleDrawUpdate);
        map.off('draw.delete', handleDrawDelete);
        map.removeControl(drawRef.current as unknown as IControl);
        drawRef.current = null;
      } catch (error) {
        // Map might already be destroyed, ignore cleanup errors
        // Silently fail - this is expected when navigating away
      }
    };
  }, [map, onGeofenceDrawn, drawMode]);

  useEffect(() => {
    if (!map || !drawRef.current || !initialGeometry) return;

    try {
      const draw = drawRef.current;
      draw.deleteAll();

      if (initialGeometry.type === 'polygon') {
        const polygonGeometry = initialGeometry as GeofencePolygonGeometry;
        draw.add({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: polygonGeometry.coordinates
          }
        });
      } else {
        const circleGeometry = initialGeometry as GeofenceCircleGeometry;
        const points = createCirclePoints(circleGeometry.center, circleGeometry.radius);
        draw.add({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [points]
          }
        });
      }
      
      // If readOnly, change mode to simple_select (static)
      if (readOnly) {
          // This disables editing interaction
          // We can't fully "disable" it via public API easily without custom modes,
          // but simple_select without controls is close.
          // Actually 'static' mode is what we want but MapboxGL Draw doesn't have a built-in 'static' mode in that name usually,
          // but we can just not add controls and set mode to simple_select.
          // However, users can still click and select.
          // To make it truly read-only, we might rely on the map not having the controls and maybe suppressing events if needed.
          // For now, just hiding controls and setting mode is a good start.
          // There is a 'static' mode in the styles filter, let's see if we can use that.
          // Actually, Mapbox Draw usually has a 'static' mode or we can just not add it to the map if we only want to render?
          // But we want to render the polygon using the same logic.
          // Let's just stick to simple_select.
          draw.changeMode('simple_select');
      }
    } catch (error) {
      // Map might be destroyed, ignore errors
      console.warn('Error setting initial geometry:', error);
    }
  }, [map, initialGeometry, readOnly]);

  const handleModeChange = (mode: 'polygon' | 'circle') => {
    if (!map || !drawRef.current || readOnly) return;

    const draw = drawRef.current;
    setDrawMode(mode);
    draw.deleteAll();
    draw.changeMode('draw_polygon');
  };

  if (readOnly) return null;

  return (
    <div className="absolute top-4 left-4 z-10 flex gap-2">
      <Button
        variant={drawMode === 'polygon' ? 'default' : 'outline'}
        onClick={() => handleModeChange('polygon')}
      >
        Draw Polygon
      </Button>
      {/* <Button
        variant={drawMode === 'circle' ? 'default' : 'outline'}
        onClick={() => handleModeChange('circle')}
      >
        Draw Circle
      </Button> */}
    </div>
  );
} 